import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useInvoices, useNextInvoiceNumber, useSaveInvoice } from '../hooks/useInvoices';
import { useServiceCatalog } from '@/features/quotes/hooks/useServiceCatalog';
import { useClients } from '@/features/clients/hooks/useClients';
import { FileText, Plus, Trash2, ArrowLeft, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import type { InvoiceItem } from '../services/generateInvoicePdf';

const VAT_RATES = [0, 5.5, 10, 20];

const emptyLine = (): InvoiceItem => ({
  description: '',
  quantity: 1,
  unitPrice: 0,
  vatRate: 0,
});

function lineTotal(item: InvoiceItem) {
  const ht = item.quantity * item.unitPrice;
  return { ht, ttc: ht * (1 + item.vatRate / 100) };
}

export default function InvoiceNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdParam  = searchParams.get('clientId');
  const sessionIdParam = searchParams.get('sessionId');
  const editId         = searchParams.get('edit');

  const { data: invoices = [] } = useInvoices();
  const { clients = [] } = useClients();
  const { data: catalog = [] } = useServiceCatalog();
  const nextInvoiceNumber = useNextInvoiceNumber();
  const saveInvoice = useSaveInvoice();

  const [clientId, setClientId]           = useState<string | null>(clientIdParam);
  const [clientName, setClientName]       = useState('');
  const [clientEmail, setClientEmail]     = useState('');
  const [items, setItems]                 = useState<InvoiceItem[]>([emptyLine()]);
  const [depositAmount, setDepositAmount] = useState('');
  const [dueDate, setDueDate]             = useState('');
  const [notes, setNotes]                 = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [linkedSessionId, setLinkedSessionId] = useState<string | null>(sessionIdParam);

  const { data: sessionData } = useQuery({
    queryKey: ['invoice-prefill-session', sessionIdParam],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('sessions')
        .select('id, client_id, client_name, project_name, body_zone, style, act_type')
        .eq('id', sessionIdParam)
        .single();
      return data ?? null;
    },
    enabled: !!sessionIdParam && !editId,
  });

  useEffect(() => {
    if (!sessionData) return;
    if (sessionData.client_id && !clientIdParam) setClientId(sessionData.client_id);
    if (sessionData.client_name) setClientName(sessionData.client_name);
    setLinkedSessionId(sessionData.id);
    const description = [sessionData.project_name, sessionData.body_zone, sessionData.style ?? sessionData.act_type]
      .filter(Boolean).join(' — ');
    setItems([{ description: description || 'Prestation', quantity: 1, unitPrice: 0, vatRate: 20 }]);
  }, [sessionData]);

  useEffect(() => {
    if (editId && invoices.length > 0) {
      const inv = invoices.find((x) => x.id === editId);
      if (inv) {
        setClientId(inv.client_id);
        setClientName(inv.client_name);
        setClientEmail(inv.client_email);
        setItems(inv.items.length > 0 ? inv.items : [emptyLine()]);
        setDepositAmount(inv.deposit_amount > 0 ? String(inv.deposit_amount) : '');
        setDueDate(inv.due_date || '');
        setNotes(inv.notes);
        setInvoiceNumber(inv.invoice_number);
      }
    }
  }, [editId, invoices]);

  useEffect(() => {
    if (clientId) {
      const c = clients.find((x) => x.id === clientId);
      if (c) {
        setClientName(c.name);
        setClientEmail((c as any).email || '');
      }
    }
  }, [clientId, clients]);

  const totals = items.reduce(
    (acc, item) => {
      const { ht, ttc } = lineTotal(item);
      return {
        total_ht: acc.total_ht + ht,
        total_vat: acc.total_vat + (ttc - ht),
        total_ttc: acc.total_ttc + ttc,
      };
    },
    { total_ht: 0, total_vat: 0, total_ttc: 0 },
  );

  const updateItem = (index: number, field: keyof InvoiceItem, value: number | string) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addFromCatalog = (catalogId: string) => {
    const cat = catalog.find((c) => c.id === catalogId);
    if (!cat) return;
    setItems((prev) => [
      ...prev.filter((i) => i.description !== ''),
      { description: cat.name, quantity: 1, unitPrice: cat.default_price, vatRate: cat.vat_rate },
    ]);
  };

  const handleSubmit = async (status: 'draft' | 'sent') => {
    if (!clientName.trim()) {
      toast.error('Le nom du client est requis');
      return;
    }
    if (items.every((i) => !i.description.trim())) {
      toast.error('Ajoutez au moins une prestation');
      return;
    }
    if (depositAmount && parseFloat(depositAmount) > totals.total_ttc) {
      toast.error("L'acompte ne peut pas dépasser le montant total TTC");
      return;
    }
    if (dueDate && dueDate < new Date().toISOString().split('T')[0]) {
      toast.error("La date d'échéance ne peut pas être dans le passé");
      return;
    }

    let number = invoiceNumber;
    if (!number) {
      try {
        number = await nextInvoiceNumber.mutateAsync();
      } catch (err: any) {
        toast.error(`Erreur numérotation : ${err?.message}`);
        return;
      }
    }

    const computedItems = items.filter((i) => i.description.trim());

    saveInvoice.mutate(
      {
        id: editId || undefined,
        invoice_number: number,
        session_id: linkedSessionId,
        client_id: clientId,
        client_name: clientName,
        client_email: clientEmail,
        items: computedItems,
        total_ht: totals.total_ht,
        total_vat: totals.total_vat,
        total_ttc: totals.total_ttc,
        deposit_amount: parseFloat(depositAmount) || 0,
        due_date: dueDate || null,
        notes,
        status,
        issue_date: new Date().toISOString().split('T')[0],
      },
      {
        onSuccess: () => {
          toast.success(status === 'sent' ? 'Facture enregistrée et marquée envoyée' : 'Brouillon enregistré');
          navigate('/invoices');
        },
        onError: (err: any) => toast.error(`Erreur : ${err?.message}`),
      },
    );
  };

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto space-y-6 pb-24">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')}>
          <ArrowLeft size={20} />
        </Button>
        <FileText className="text-primary" size={22} />
        <h1 className="text-xl font-bold font-display">
          {editId ? 'Modifier la facture' : 'Nouvelle facture'}
        </h1>
      </div>

      {/* Client */}
      <div className="glass-card p-5 space-y-4">
        <h2 className="font-semibold">Client</h2>
        <Select value={clientId ?? ''} onValueChange={(id) => setClientId(id || null)}>
          <SelectTrigger>
            <SelectValue placeholder="Choisir un client existant…" />
          </SelectTrigger>
          <SelectContent>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}{c.phone ? ` · ${c.phone}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Nom *</Label>
            <Input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Nom complet"
            />
          </div>
          <div className="space-y-1">
            <Label>Email</Label>
            <Input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="email@exemple.com"
            />
          </div>
        </div>
      </div>

      {/* Lignes de prestation */}
      <div className="glass-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Prestations</h2>
          {catalog.length > 0 && (
            <Select onValueChange={addFromCatalog}>
              <SelectTrigger className="w-auto h-8 px-3 text-sm gap-1">
                <ChevronDown size={14} />
                <SelectValue placeholder="Depuis le catalogue" />
              </SelectTrigger>
              <SelectContent>
                {catalog.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} — {c.default_price.toFixed(2)} €
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="grid grid-cols-[1fr_72px_88px_72px_36px] gap-2 items-end">
              <div className="space-y-1">
                {i === 0 && <Label className="text-xs text-muted-foreground">Description</Label>}
                <Input
                  value={item.description}
                  onChange={(e) => updateItem(i, 'description', e.target.value)}
                  placeholder="Prestation…"
                />
              </div>
              <div className="space-y-1">
                {i === 0 && <Label className="text-xs text-muted-foreground">Qté</Label>}
                <Input
                  type="number"
                  min={0}
                  step="0.5"
                  value={item.quantity}
                  onChange={(e) => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1">
                {i === 0 && <Label className="text-xs text-muted-foreground">Prix HT €</Label>}
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={item.unitPrice}
                  onChange={(e) => updateItem(i, 'unitPrice', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1">
                {i === 0 && <Label className="text-xs text-muted-foreground">TVA %</Label>}
                <Select
                  value={String(item.vatRate)}
                  onValueChange={(v) => updateItem(i, 'vatRate', parseFloat(v))}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VAT_RATES.map((r) => (
                      <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-10 w-9"
                onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))}
                disabled={items.length === 1}
              >
                <Trash2 size={14} className="text-destructive" />
              </Button>
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setItems((p) => [...p, emptyLine()])}
        >
          <Plus size={14} /> Ajouter une ligne
        </Button>

        <div className="border-t pt-3 space-y-1 text-sm text-right">
          <p className="text-muted-foreground">
            Sous-total HT :{' '}
            <span className="tabular-nums font-medium text-foreground">
              {totals.total_ht.toFixed(2)} €
            </span>
          </p>
          <p className="text-muted-foreground">
            TVA :{' '}
            <span className="tabular-nums font-medium text-foreground">
              {totals.total_vat.toFixed(2)} €
            </span>
          </p>
          <p className="text-base font-bold">
            Total TTC : <span className="tabular-nums">{totals.total_ttc.toFixed(2)} €</span>
          </p>
        </div>
      </div>

      {/* Conditions */}
      <div className="glass-card p-5 space-y-4">
        <h2 className="font-semibold">Conditions</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Acompte déjà versé (€)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="space-y-1">
            <Label>Date d'échéance</Label>
            <Input
              type="date"
              min={new Date().toISOString().split('T')[0]}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Notes</Label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Conditions de paiement, mentions légales…"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => handleSubmit('draft')}
          disabled={saveInvoice.isPending || nextInvoiceNumber.isPending}
        >
          Enregistrer brouillon
        </Button>
        <Button
          className="flex-1"
          onClick={() => handleSubmit('sent')}
          disabled={saveInvoice.isPending || nextInvoiceNumber.isPending}
        >
          Enregistrer et envoyer
        </Button>
      </div>
    </div>
  );
}
